import { atom, action, onDisconnect, withReset } from '@reatom/framework';
import type { GameApi, DownedFigures } from '@whitesharx/starfall-frontend-chessground';
import { GameResult, MoveHistory } from './types';

type GameAPI = {
  gameStarted?: boolean;
};

export const cgApiAtom = atom<GameApi | null>(null, 'cgApiAtom').pipe(withReset());
onDisconnect(cgApiAtom, cgApiAtom.reset);

export const gameApiAtom = atom<GameAPI | null>(null, 'gameApiAtom');

export const downedFiguresAtom = atom<DownedFigures | undefined>(undefined, 'downedFiguresAtom');

export const updateDownedFiguresAction = action(
  (ctx, figures: DownedFigures | undefined) => downedFiguresAtom(ctx, figures),
  'updateDownedFiguresAction'
);

export const gameResultAtom = atom<GameResult | null>(null, 'gameResultAtom').pipe(withReset());
onDisconnect(gameResultAtom, gameResultAtom.reset);

export const showGameResultAction = action((ctx, result: GameResult) => {
  gameResultAtom(ctx, result);
}, 'showGameResultAction');

export const hideGameResultAction = action((ctx) => {
  gameResultAtom(ctx, null);
}, 'hideGameResultAction');

export const movesHistoryAtom = atom([] as MoveHistory[], 'movesHistoryAtom').pipe(withReset());
onDisconnect(movesHistoryAtom, movesHistoryAtom.reset);

export const updateMovesHistoryAction = action((ctx, moves: MoveHistory[]) => {
  movesHistoryAtom(ctx, moves);
  selectMoveAction(ctx, moves[moves.length - 1]);
}, 'updateMovesHistoryAction');

export const selectedMoveAtom = atom<MoveHistory | undefined>(undefined, 'selectedMoveAtom');

export const selectMoveAction = action((ctx, move: MoveHistory, animate = false) => {
  const cgApi = ctx.get(cgApiAtom);
  const history = ctx.get(movesHistoryAtom);
  const disable = history.findIndex((m) => m.fen === move.fen) !== history.length - 1;

  selectedMoveAtom(ctx, move);

  if (animate) {
    cgApi?.set(
      { fen: move.fen, lastMove: { san: move.san, fen: move.beforeMoveFen } },
      { turnColor: cgApi.state.turnColor, ...(disable ? { viewOnly: true, selected: undefined } : {}) },
      false
    );
  } else if (disable) {
    cgApi?.set({}, { viewOnly: true, selected: undefined }, false);
  }
}, 'selectMoveAction');

export const rewindAction = action((ctx, type: 'start' | 'prev' | 'next' | 'end') => {
  const history = ctx.get(movesHistoryAtom);
  const currentMove = ctx.get(selectedMoveAtom);
  const currentIndex = history.findIndex((m) => m.fen === currentMove?.fen);

  if (history.length === 0) {
    return;
  }

  if (type === 'start') {
    selectMoveAction(ctx, history[0], true);
  }
  if (type === 'prev' && currentIndex !== 0) {
    selectMoveAction(ctx, history[currentIndex - 1], true);
  }
  if (type === 'next' && currentIndex !== history.length - 1) {
    selectMoveAction(ctx, history[currentIndex + 1], true);
  }
  if (type === 'end') {
    selectMoveAction(ctx, history[history.length - 1], true);
  }
}, 'rewindAction');
